import { useCallback, useRef, useState } from 'react'
import { MediaFeed } from '../api/feed'
import { targetFor, type PreloadProgress, type PreloadTag } from '../api/preload'
import type { ContentKind, MediaItem } from '../types'

const BG_PAGE_GAP_MS = 220

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function useMediaFeed() {
  const feedRef = useRef<MediaFeed | null>(null)
  const [items, setItems] = useState<MediaItem[]>([])
  const [progress, setProgress] = useState<PreloadProgress>({
    loadedMs: 0,
    targetMs: 1,
    count: 0,
    fraction: 0,
  })
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const abortRef = useRef<AbortController | null>(null)
  const bgRef = useRef(false)

  const sync = useCallback((feed: MediaFeed, targetMs: number) => {
    setItems([...feed.queue])
    setHasMore(feed.hasMore)
    setProgress({
      loadedMs: feed.loadedMs,
      targetMs,
      count: feed.count,
      fraction: Math.min(1, feed.count / Math.max(24, Math.ceil(targetMs / 4000))),
    })
  }, [])

  const continueInBackground = useCallback(
    (feed: MediaFeed, targetMs: number, signal: AbortSignal) => {
      if (bgRef.current) return
      bgRef.current = true
      void (async () => {
        try {
          while (feed.hasMore && !signal.aborted) {
            const added = await feed.fetchNextPage()
            if (added > 0) sync(feed, targetMs)
            if (added === 0) break
            await sleep(BG_PAGE_GAP_MS)
          }
        } finally {
          bgRef.current = false
          if (!signal.aborted) sync(feed, targetMs)
        }
      })()
    },
    [sync],
  )

  const startBrowse = useCallback(
    async (tags: PreloadTag[], kind: ContentKind, minItems = 24) => {
      abortRef.current?.abort()
      bgRef.current = false
      const ac = new AbortController()
      abortRef.current = ac
      setLoading(true)
      setError(null)
      setItems([])
      const targetMs = minItems * 4000
      setProgress({ loadedMs: 0, targetMs, count: 0, fraction: 0 })

      const feed = new MediaFeed({
        tags,
        kind,
        signal: ac.signal,
        onProgress: setProgress,
      })
      feedRef.current = feed

      try {
        await feed.loadUntil(minItems, targetMs)
        if (ac.signal.aborted) return null
        if (!feed.count) {
          setError('No media found for those tags. Try different ones!')
          return null
        }
        sync(feed, targetMs)
        continueInBackground(feed, targetMs, ac.signal)
        return feed
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return null
        setError(e instanceof Error ? e.message : 'load failed')
        return null
      } finally {
        setLoading(false)
      }
    },
    [sync, continueInBackground],
  )

  const startGame = useCallback(
    async (tags: PreloadTag[], kind: ContentKind) => {
      abortRef.current?.abort()
      bgRef.current = false
      const ac = new AbortController()
      abortRef.current = ac
      setLoading(true)
      setError(null)
      setItems([])
      const targetMs = targetFor(kind)
      setProgress({ loadedMs: 0, targetMs, count: 0, fraction: 0 })

      const feed = new MediaFeed({
        tags,
        kind,
        signal: ac.signal,
        onProgress: setProgress,
      })
      feedRef.current = feed

      try {
        await feed.loadUntil(20, targetMs)
        if (ac.signal.aborted) return null
        if (!feed.count) {
          setError('No media found for those tags. Try different ones!')
          return null
        }
        sync(feed, targetMs)
        continueInBackground(feed, targetMs, ac.signal)
        return feed.queue
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return null
        setError(e instanceof Error ? e.message : 'load failed')
        return null
      } finally {
        setLoading(false)
      }
    },
    [sync, continueInBackground],
  )

  const loadMore = useCallback(async (batchPages = 3) => {
    const feed = feedRef.current
    if (!feed || !feed.hasMore || loadingMore) return 0
    setLoadingMore(true)
    try {
      let added = 0
      for (let i = 0; i < batchPages && feed.hasMore; i++) {
        added += await feed.fetchNextPage()
      }
      sync(feed, progress.targetMs)
      return added
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, progress.targetMs, sync])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    feedRef.current = null
    bgRef.current = false
    setLoading(false)
    setLoadingMore(false)
  }, [])

  return {
    items,
    progress,
    loading,
    loadingMore,
    hasMore,
    error,
    setError,
    startBrowse,
    startGame,
    loadMore,
    cancel,
    feedRef,
  }
}